// Requirement 21 in the apps (web app and desktop): the platform's maintenance notices as a banner above every screen —
// start and end converted to this device's own time zone, the services affected, a sync warning (offline changes are kept
// on this device and sent when the service returns), and «service restored» once the owner marks the work completed.
// Each notice is dismissible per phase (dismissing «upcoming» still shows «ongoing» and «restored» once).
// The last list read is kept on the device, so a notice stays visible while the platform itself is down for the work.
import { useEffect, useState } from 'react';
import { deviceTimeZone, noticeKey, noticeWindow, parseNotices, visibleNotices, type MaintenanceNotice, type NoticePhase } from '@vero/platform';
import { useRuntime } from '../core/runtime.tsx';
import { Icon } from '../core/icons.tsx';
import { accountService, sameOriginPlatform, PLATFORM_CONFIG } from '../core/account-service.ts';
import { lang3 } from '../guide/l3.ts';

const CACHE = 'vero.notices';
const DISMISSED = 'vero.notices.dismissed';
const read = <T,>(k: string, d: T): T => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) as T : d; } catch { return d; } };
const write = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage blocked: the banner still works for this session */ } };

/** the platform this device talks to (signed-in server, the platform that serves the web app, or the build's default) */
export function platformBase(): string | null {
  const u = accountService()?.serverUrl ?? sameOriginPlatform() ?? PLATFORM_CONFIG.url;
  return u ? u.replace(/\/+$/, '') : null;
}
export async function fetchNotices(base = platformBase(), f: typeof fetch = fetch): Promise<MaintenanceNotice[] | null> {
  if (!base || typeof f !== 'function') return null;
  try { const r = await f(`${base}/v1/platform/notices`, { cache: 'no-store' }); return r.ok ? parseNotices(await r.json()) : null; } catch { return null; }
}

const sev = (n: MaintenanceNotice, phase: NoticePhase) => (phase === 'restored' ? 'ok' : n.severity === 'major' ? 'crit' : n.severity === 'minor' ? 'warn' : '');

export function MaintenanceBanner() {
  const rt = useRuntime();
  const { t } = rt;
  const [list, setList] = useState<MaintenanceNotice[]>(() => parseNotices(read<unknown>(CACHE, [])));
  const [dismissed, setDismissed] = useState<string[]>(() => read<string[]>(DISMISSED, []));
  const [now, setNow] = useState(() => new Date());
  // the platform can change (sign-in to another server): read again then
  const [base, setBase] = useState(platformBase);
  useEffect(() => accountService()?.subscribe(() => setBase(platformBase())), []);
  useEffect(() => {
    let on = true;
    const load = async () => { const n = await fetchNotices(base); if (on && n) { setList(n); write(CACHE, n); } };
    void load();
    const every = setInterval(() => void load(), 10 * 60_000);
    const tick = setInterval(() => setNow(new Date()), 60_000); // upcoming → ongoing at the start time, even offline
    const online = () => void load();
    window.addEventListener('online', online);
    return () => { on = false; clearInterval(every); clearInterval(tick); window.removeEventListener('online', online); };
  }, [base]);
  const shown = visibleNotices(list, now, new Set(dismissed));
  if (!shown.length) return null;
  const zone = deviceTimeZone();
  const lang = lang3(rt.locale);
  const dismiss = (k: string) => { const next = [...new Set([...dismissed, k])].slice(-100); setDismissed(next); write(DISMISSED, next); };
  return <div className="stack" style={{ gap: 8, marginBottom: 14 }}>
    {shown.map(({ notice: n, phase }) => {
      const w = noticeWindow(n, rt.locale, zone);
      const k = noticeKey(n, phase);
      return (
        <div key={k} className={`callout maint-banner ${sev(n, phase)}`} role={phase === 'ongoing' ? 'alert' : 'status'} data-testid="maint-banner" data-phase={phase}>
          <Icon name={phase === 'restored' ? 'check' : 'bolt'} size={16} />
          <div style={{ flex: 1 }}>
            <b>{t(`maint.phase.${phase}`)}</b>
            {phase !== 'restored' && <div className="small" data-testid="maint-window">{t('maint.window', { window: w.text })} <span className="muted">· {t('maint.your_zone', { zone })}</span></div>}
            {phase !== 'restored' && n.services.length > 0 && <div className="small" data-testid="maint-services">{t('maint.services', { services: n.services.map((s) => t(`maint.service.${s}`)).join(', ') })}</div>}
            {(n.message[lang] || n.message.en) && <div className="small">{n.message[lang] || n.message.en}</div>}
            {phase !== 'restored' && (n.services.includes('sync') || n.services.includes('web_app') || n.services.includes('account')) && <div className="small" data-testid="maint-sync">{t('maint.sync_warning')}</div>}
            {phase === 'restored' && <div className="small" data-testid="maint-restored">{t('maint.restored_body')}</div>}
          </div>
          <button className="btn btn-icon btn-sm" aria-label={t('maint.dismiss')} title={t('maint.dismiss')} onClick={() => dismiss(k)} data-testid="maint-dismiss"><Icon name="close" size={14} /></button>
        </div>
      );
    })}
  </div>;
}
